import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Library, Mail, Lock } from 'lucide-react';
import { useLanguage } from '@/i18n';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      execute: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const GENERIC_AUTH_ERROR = 'Authentication failed.';

function isLocalhost() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

function allowDevDirectLogin() {
  return import.meta.env.VITE_ALLOW_DEV_DIRECT_LOGIN === 'true' && isLocalhost();
}

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const turnstileResolveRef = useRef<((token: string) => void) | null>(null);
  const turnstileRejectRef = useRef<((error: Error) => void) | null>(null);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  useEffect(() => {
    if (allowDevDirectLogin() || !turnstileSiteKey || !turnstileContainerRef.current) return;

    const renderWidget = () => {
      if (!window.turnstile || !turnstileContainerRef.current || turnstileWidgetIdRef.current) return;

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        size: 'invisible',
        callback: (token: string) => {
          turnstileResolveRef.current?.(token);
          turnstileResolveRef.current = null;
          turnstileRejectRef.current = null;
        },
        'error-callback': () => {
          turnstileRejectRef.current?.(new Error(GENERIC_AUTH_ERROR));
          turnstileResolveRef.current = null;
          turnstileRejectRef.current = null;
        },
        'expired-callback': () => {
          if (turnstileWidgetIdRef.current) {
            window.turnstile?.reset(turnstileWidgetIdRef.current);
          }
        },
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    let script = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    script.addEventListener('load', renderWidget);
    return () => {
      script?.removeEventListener('load', renderWidget);
    };
  }, [turnstileSiteKey]);

  useEffect(() => {
    if (user) {
      navigate('/admin');
    }
  }, [navigate, user]);

  const executeTurnstile = async () => {
    if (allowDevDirectLogin()) return '';
    // Turnstile is optional: when no site key is configured, skip it entirely
    // so the flow proceeds without a token. The server only enforces Turnstile
    // when TURNSTILE_SECRET_KEY is configured.
    if (!turnstileSiteKey) return '';
    if (!window.turnstile || !turnstileWidgetIdRef.current) {
      throw new Error(GENERIC_AUTH_ERROR);
    }

    return await new Promise<string>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        turnstileResolveRef.current = null;
        turnstileRejectRef.current = null;
        reject(new Error(GENERIC_AUTH_ERROR));
      }, 10000);

      turnstileResolveRef.current = (token) => {
        window.clearTimeout(timeout);
        resolve(token);
      };
      turnstileRejectRef.current = (error) => {
        window.clearTimeout(timeout);
        reject(error);
      };

      window.turnstile?.execute(turnstileWidgetIdRef.current as string);
    });
  };

  if (user) {
    return null;
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('pleaseEnterEmailPassword'));
      return;
    }

    try {
      setIsLoading(true);
      const turnstileToken = await executeTurnstile();
      await signIn(email, password, turnstileToken);
      navigate('/admin');
    } catch (error) {
      // Error is already handled in the auth context
      console.warn(GENERIC_AUTH_ERROR);
      if (turnstileWidgetIdRef.current) {
        window.turnstile?.reset(turnstileWidgetIdRef.current);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 bg-gradient-to-br from-background via-background to-secondary/20">
      <div className="w-full max-w-md mx-auto space-y-6">
        <div className="text-center flex flex-col items-center">
          <div className="bg-primary rounded-xl p-3 mb-4 text-primary-foreground">
            <Library size={28} />
          </div>
          <h1 className="text-3xl font-bold tracking-tighter mb-1">{t('panelTitle')}</h1>
          <p className="text-sm text-muted-foreground max-w-xs mx-auto">
            {t('enterCredentials')}
          </p>
        </div>

        <div className="bg-card p-6 rounded-xl shadow-subtle border border-border/40 overflow-hidden backdrop-blur-sm">
          <form onSubmit={handleSignIn} className="space-y-5 pt-2">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Mail size={14} className="text-muted-foreground" />
                {t('email')}
              </label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="bg-background/70"
                required
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Lock size={14} className="text-muted-foreground" />
                  {t('password')}
                </label>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="bg-background/70"
                required
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full transition-all mt-4"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></span>
                  {t('signingIn')}
                </span>
              ) : (
                t('signIn')
              )}
            </Button>
            <div ref={turnstileContainerRef} className="hidden" aria-hidden="true" />
          </form>
        </div>
        
        <div className="text-center text-xs text-muted-foreground mt-8">
          <p>&copy; {new Date().getFullYear()} aliofficials. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
